import { Button, Checkbox, FormControl, Grid, Switch, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { DetailedErrorDialog } from '~/components/detailed-error-dialog';
import { EmptyUser, type Role, type User, UserService } from '~/services/user';

const FormGroupPadded = styled(FormGroup)({
  marginBottom: '4px',
});

const useStyles = makeStyles()({
  noTopMargin: {
    marginTop: 0,
  },
});

export default function UserDetailPage(props: any) {
  const { match } = props;
  const [user, setUser] = useState(EmptyUser);
  const [roles, setRoles] = useState<Role[]>([]);
  const [updated] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [error, setError] = useState(undefined);
  const [forcePasswordUpdate, setForcePasswordUpdate] = useState(true);

  const lengthRegexp = /(\S){7,32}/;
  const lowercaseRegexp = /((\S)*[a-zåäö]+(\S)*)/;
  const uppercaseRegexp = /((\S)*[A-ZÅÄÖ]+(\S)*)/;
  const characterTypeCheck = /(\d|\W)+/;

  const errorDialog = React.createRef<DetailedErrorDialog>();

  useEffect(() => {
    const subscription = UserService.getRoles().subscribe((dbRoles) => setRoles(dbRoles));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (updated || props.location.state === undefined) {
      if (match.params.id === undefined) {
        return;
      }

      const subscription = UserService.get(match.params.id).subscribe(
        (dbUser: User) => {
          setPasswordValid(true);
          setForcePasswordUpdate(false);
          setUser(dbUser);
        },
        (err) => {
          setError(err);
        }
      );
      return () => subscription.unsubscribe();
    } else {
      setPasswordValid(true);
      setForcePasswordUpdate(false);
      setUser(props.location.state.user);
      return;
    }
  }, [updated]);

  const handleChange = (property: keyof User) => (event: React.ChangeEvent<any>) => {
    const userClone = { ...user, [property]: event.target.value };

    setUser(userClone);
  };

  const validatePassword = (password: string) => {
    return (
      lengthRegexp.test(password) &&
      lowercaseRegexp.test(password) &&
      uppercaseRegexp.test(password) &&
      characterTypeCheck.test(password)
    );
  };

  const handlePasswordChange = () => (event: React.ChangeEvent<any>) => {
    const password = event.target.value;

    let valid = validatePassword(password);
    if (!forcePasswordUpdate && password === '') {
      valid = true;
    }
    setPasswordValid(valid);

    const userClone = { ...user, password: password };

    setUser(userClone);
  };

  const handleChangeSwitch = (property: keyof User) => (event: React.ChangeEvent<any>) => {
    const userClone = { ...user, [property]: event.target.checked };

    setUser(userClone);
  };

  const handleRoleChange = (role: Role) => (event: React.ChangeEvent<any>) => {
    if (event.target.checked) {
      user.roles.push(role);
    } else {
      user.roles.splice(
        user.roles.findIndex((userRole) => userRole.id === role.id),
        1
      );
    }

    setUser({ ...user });
  };

  const deleteUser = () => {
    if (!confirm('Är du säker?')) {
      return;
    }

    UserService.delete(user).subscribe(() => props.history.push('/users'));
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    UserService.save(user).subscribe(
      () => props.history.push('/users'),
      (err) => {
        if (errorDialog.current) {
          errorDialog.current.openDialog({
            message: err.message,
            details: !err.data.username
              ? JSON.stringify(err.data)
              : `Användarnamnet \"${err.data.username}\" är redan upptaget.`,
            name: 'SaveUserError',
          });
        }
      }
    );
  };

  if (error !== undefined) {
    return <Typography>Användaren finns inte.</Typography>;
  }

  const { classes } = useStyles();

  return (
    <React.Fragment>
      <form autoComplete="off" onSubmit={(event) => submit(event)}>
        <FormGroupPadded>
          <TextField
            variant="standard"
            margin="normal"
            label="Användarnamn / E-post"
            value={user.principal}
            helperText="Det här fältet identifierar användaren och om det är en giltig epostadress används det till att skicka notifikationer."
            error={!user.principal}
            onChange={handleChange('principal')}
          />
        </FormGroupPadded>
        <FormGroupPadded>
          <TextField
            variant="standard"
            margin="normal"
            label="Namn"
            value={user.fullName}
            helperText="Namn på användaren."
            onChange={handleChange('fullName')}
          />
        </FormGroupPadded>
        <FormGroupPadded>
          <TextField
            variant="standard"
            margin="dense"
            label="Lösenord (fyll bara i om du vill ändra användarens lösenord)"
            value={user.password}
            autoComplete="new-password"
            type="password"
            error={!passwordValid}
            helperText={
              <>
                Lösenord för användaren.
                <br />
                7-32 tecken. 1+ gemener. 1+ versaler. 1+ tecken.
              </>
            }
            onChange={handlePasswordChange()}
          />
        </FormGroupPadded>
        <FormGroupPadded>
          <FormControlLabel
            control={<Switch checked={user.locked} onChange={handleChangeSwitch('locked')} />}
            label="Låst konto"
          />
          <FormHelperText>
            Om ett konto är låst går det inte att logga in eller komma åt något i systemet.
          </FormHelperText>
        </FormGroupPadded>
        <FormGroupPadded>
          <FormControlLabel
            control={<Switch checked={user.use2Factor} onChange={handleChangeSwitch('use2Factor')} />}
            label="Tvåfaktorsauthentisering"
          />
          <FormHelperText>Om aktivt måste man ange en extra kod för att logga in.</FormHelperText>
        </FormGroupPadded>
        <FormGroupPadded>
          <FormControlLabel
            control={<Switch checked={user.tmpPassword} onChange={handleChangeSwitch('tmpPassword')} />}
            label="Återställt lösenord"
          />
          <FormHelperText>
            Om användaren behöver byta lösenord nästa gång de loggar in, kryssa för det här alternativet. Detta skickar
            INTE ut ett e-post med återställningsinformation. <br />
            Detta är typiskt bra om lösenordet ändrats här ovanför.
          </FormHelperText>
        </FormGroupPadded>
        <Grid item md={4}>
          <FormControl variant="standard" component={'fieldset' as 'div'}>
            <FormLabel component="legend">Roller</FormLabel>
            <FormGroupPadded>
              {roles.length > 0 &&
                roles.map((role) => (
                  <React.Fragment key={role.id}>
                    <FormControlLabel
                      control={<Checkbox value={role.id} />}
                      label={role.name}
                      checked={user.roles.some((userRole) => userRole.id === role.id)}
                      onChange={handleRoleChange(role)}
                    />
                    <FormHelperText className={classes.noTopMargin}>{role.description}</FormHelperText>
                  </React.Fragment>
                ))}
            </FormGroupPadded>
          </FormControl>
        </Grid>
        <Button type="submit" disabled={!passwordValid || !user.principal}>
          Spara
        </Button>
        {user.id !== 0 && (
          <Button color={'secondary'} onClick={() => deleteUser()}>
            Ta bort
          </Button>
        )}
      </form>
      <DetailedErrorDialog ref={errorDialog} />
    </React.Fragment>
  );
}
